import React, { createContext, useState, useContext, useEffect } from "react";
import { calculateDate } from "libs/dateCalculator";
import { estadosSolicitud } from "components/business/estadoSolicitudDropdown/EstadoSolicitudDropdown";
import { Provincia } from "components/business/provinciaSelector/getProvincias";
import { IListItem } from "api/types/originacion/commonTypes";

interface FiltrosContextType {
  search: string;
  setSearch: (value: string) => void;
  fechaDesde: Date;
  fechaHasta: Date;
  fechaDesdeObOnline: Date;
  fechaHastaObOnline: Date;
  estadoSolicitud: string;
  fechaEstado: string;
  provincia: Provincia | null;
  setProvincia: (provincia: Provincia | null) => void;
  currentPage: number;
  rowsPerPage: number;
  setFechaDesde: (date: Date) => void;
  setFechaHasta: (date: Date) => void;
  setFechaDesdeObOnline: (date: Date) => void;
  setFechaHastaObOnline: (date: Date) => void;
  setEstadoSolicitud: (estado: string) => void;
  setFechaEstado: (fecha: string) => void;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  handleChangeInterval: (value: number | null) => void;
  handleChangeStartDate: (date: string) => void;
  handleChangeEndDate: (date: string) => void;
  handleChangeIntervalObOnline: (value: number | null) => void;
  handleChangeStartDateObOnline: (date: string) => void;
  handleChangeEndDateObOnline: (date: string) => void;
  onPageChange: (e: any, n: number) => void;
  onRowsPerPageChange: (e: any) => void;
  ITEMS_PER_PAGE: number[];
  fechaLoteSeleccionada: string;
  setFechaLoteSeleccionada: (value: string) => void;
  cadenaSeleccionada: IListItem | null;
  setCadenaSeleccionada: (value: IListItem | null) => void;
  ITEMS_PER_PAGE_OB: number[];
  currentPageOb: number;
  rowsPerPageOb: number;
  onPageChangeOb: (e: any, n: number) => void;
  onRowsPerPageChangeOb: (e: any) => void;
  searchObOnline: string;
  setSearchObOnline: (value: string) => void;
}

const FiltrosContext = createContext<FiltrosContextType | undefined>(undefined);

export const FiltrosProvider: React.FC = ({ children }) => {
  // State for search input Solicitudes
  const [search, setSearch] = useState("");

  // State for search input OB Online
  const [searchObOnline, setSearchObOnline] = useState("");

  // State for start and end dates Solicitudes
  const [fechaDesde, setFechaDesde] = useState(calculateDate(new Date(), -30));
  const [fechaHasta, setFechaHasta] = useState(new Date());

  // State for start and end dates OB Online
  const [fechaDesdeObOnline, setFechaDesdeObOnline] = useState(
    calculateDate(new Date(), -30),
  );
  const [fechaHastaObOnline, setFechaHastaObOnline] = useState(new Date());

  // State for estadoSolicitud and fechaEstado
  const [estadoSolicitud, setEstadoSolicitud] = useState(
    estadosSolicitud.Consumo[0].value,
  );
  const [fechaEstado, setFechaEstado] = useState(
    estadosSolicitud.ConsumoYen[0].value,
  );

  // State for provincia filter
  const [provincia, setProvincia] = useState<Provincia | null>(null);

  // State for pagination Solicitudes
  const ITEMS_PER_PAGE = [10, 25, 50, 100];
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE[0]);

  // State for pagination OB Online
  const ITEMS_PER_PAGE_OB = [10, 25, 50, 100];
  const [currentPageOb, setCurrentPageOb] = useState(0);
  const [rowsPerPageOb, setRowsPerPageOb] = useState(ITEMS_PER_PAGE_OB[0]);

  // Reset currentPage when filters change for Solicitudes
  useEffect(() => {
    setCurrentPage(0);
  }, [estadoSolicitud, fechaDesde, fechaHasta, fechaEstado, provincia, search]);

  // Reset currentPage when search changes for OB Online
  useEffect(() => {
    setCurrentPageOb(0);
  }, [searchObOnline, fechaDesdeObOnline, fechaHastaObOnline]);

  // Adjust date range based on search input for Solicitudes
  useEffect(() => {
    if (search === "") {
      setFechaDesde(calculateDate(new Date(), -30));
    } else {
      setFechaDesde(new Date("2024-08-02"));
      setFechaHasta(new Date());
    }
  }, [search]);

  // Adjust date range based on search input for OB Online
  useEffect(() => {
    if (searchObOnline === "") {
      setFechaDesdeObOnline(calculateDate(new Date(), -30));
    } else {
      setFechaDesdeObOnline(new Date("2024-08-02"));
      setFechaHastaObOnline(new Date());
    }
  }, [searchObOnline]);

  // Handle date changes for Solicitudes
  const handleChangeInterval = (value: number | null) => {
    setFechaDesde(calculateDate(new Date(), value));
    setFechaHasta(new Date());
  };

  const handleChangeStartDate = (date: string) => {
    setFechaDesde(date ? new Date(date) : calculateDate(new Date(), -30));
  };

  const handleChangeEndDate = (date: string) => {
    setFechaHasta(date ? new Date(date) : new Date());
  };

  // Handle date changes for OB Online
  const handleChangeIntervalObOnline = (value: number | null) => {
    setFechaDesdeObOnline(calculateDate(new Date(), value));
    setFechaHastaObOnline(new Date());
  };

  const handleChangeStartDateObOnline = (date: string) => {
    setFechaDesdeObOnline(
      date ? new Date(date) : calculateDate(new Date(), -30),
    );
  };

  const handleChangeEndDateObOnline = (date: string) => {
    setFechaHastaObOnline(date ? new Date(date) : new Date());
  };

  // Handle pagination changes for Solicitudes
  const onPageChange = (e: any, n: number) => {
    setCurrentPage(n);
  };

  const onRowsPerPageChange = (e: any) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  // Handle pagination changes for OB Online
  const onPageChangeOb = (e: any, n: number) => {
    setCurrentPageOb(n);
  };
  const onRowsPerPageChangeOb = (e: any) => {
    setRowsPerPageOb(parseInt(e.target.value, 10));
    setCurrentPageOb(0);
  };

  // State for fechaLoteSeleccionada
  const [fechaLoteSeleccionada, setFechaLoteSeleccionada] =
    useState<string>("hoy");

  // State for cadenaSeleccionada
  const [cadenaSeleccionada, setCadenaSeleccionada] =
    useState<IListItem | null>({ value: 0, label: "Todas" });

  return (
    <FiltrosContext.Provider
      value={{
        search,
        setSearch,
        fechaDesde,
        fechaHasta,
        fechaDesdeObOnline,
        fechaHastaObOnline,
        estadoSolicitud,
        fechaEstado,
        provincia,
        setProvincia,
        currentPage,
        rowsPerPage,
        setFechaDesde,
        setFechaHasta,
        setFechaDesdeObOnline,
        setFechaHastaObOnline,
        setEstadoSolicitud,
        setFechaEstado,
        setCurrentPage,
        setRowsPerPage,
        handleChangeInterval,
        handleChangeStartDate,
        handleChangeEndDate,
        handleChangeEndDateObOnline,
        handleChangeStartDateObOnline,
        handleChangeIntervalObOnline,
        onPageChange,
        onRowsPerPageChange,
        ITEMS_PER_PAGE,
        fechaLoteSeleccionada,
        setFechaLoteSeleccionada,
        cadenaSeleccionada,
        setCadenaSeleccionada,
        ITEMS_PER_PAGE_OB,
        currentPageOb,
        rowsPerPageOb,
        onPageChangeOb,
        onRowsPerPageChangeOb,
        searchObOnline,
        setSearchObOnline,
      }}
    >
      {children}
    </FiltrosContext.Provider>
  );
};

export const useFiltrosContext = () => {
  const context = useContext(FiltrosContext);
  if (!context) {
    throw new Error("useFiltrosContext must be used within a FiltrosProvider");
  }
  return context;
};
